import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Copy, Check, Mail, MessageCircle, Megaphone, Linkedin, Twitter, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function PartnerKit() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useImperativeLoad((() => () => {
    api.get("/affiliate/dashboard").then((r) => setData(r.data));
  })(), []);

  if (user && (!user.is_affiliate || user.affiliate_status !== "approved")) {
    return <Navigate to="/affiliate" replace />;
  }
  if (!data) {
    return <div className="flex h-full items-center justify-center text-slate-400">{t("common.loading")}</div>;
  }

  const cobrandedLink = `${window.location.origin}${data.cobranded_path || `/p/${data.referral_code}`}`;
  const signupLink = `${window.location.origin}${data.referral_path}`;
  const partnerName = user?.name || user?.email?.split("@")[0] || "TelcaVoIP";

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="partner-kit-page">
      <header className="mb-8">
        <Link to="/partner" className="mb-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-[#9ACD32]" data-testid="kit-back-link">
          <ArrowLeft className="h-3 w-3" />
          {t("partner_kit.back")}
        </Link>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-white">{t("partner_kit.title")}</h1>
        <p className="mt-2 max-w-2xl text-slate-300">{t("partner_kit.subtitle")}</p>
      </header>

      {/* Co-branded link card */}
      <Card className="mb-8 border-slate-800 bg-gradient-to-br from-[#0A0C1F] to-[#0B0E5C]/30 p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{t("partner_kit.cobranded_label")}</p>
        <p className="mt-2 text-sm text-slate-300">{t("partner_kit.cobranded_desc")}</p>
        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
          <Input readOnly value={cobrandedLink} className="flex-1 border-slate-700 bg-slate-900 font-mono text-sm text-slate-200" data-testid="kit-cobranded-link-input" />
          <CopyButton text={cobrandedLink} testId="kit-cobranded-copy" />
          <a href={cobrandedLink} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full border-slate-700 text-slate-200" data-testid="kit-cobranded-preview">
              {t("partner_kit.preview")}
            </Button>
          </a>
        </div>
      </Card>

      <Tabs defaultValue="email" className="w-full">
        <TabsList className="mb-6 grid w-full max-w-2xl grid-cols-3 bg-slate-900">
          <TabsTrigger value="email" data-testid="kit-tab-email">
            <Mail className="mr-2 h-4 w-4" /> {t("partner_kit.tab_email")}
          </TabsTrigger>
          <TabsTrigger value="social" data-testid="kit-tab-social">
            <Megaphone className="mr-2 h-4 w-4" /> {t("partner_kit.tab_social")}
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="kit-tab-chat">
            <MessageCircle className="mr-2 h-4 w-4" /> {t("partner_kit.tab_chat")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="space-y-5">
          <TemplateList type="email" cobrandedLink={cobrandedLink} partnerName={partnerName} signupLink={signupLink} locale={locale} />
        </TabsContent>

        <TabsContent value="social" className="space-y-5">
          <TemplateList type="social" cobrandedLink={cobrandedLink} partnerName={partnerName} signupLink={signupLink} locale={locale} />
        </TabsContent>

        <TabsContent value="chat" className="space-y-5">
          <TemplateList type="chat" cobrandedLink={cobrandedLink} partnerName={partnerName} signupLink={signupLink} locale={locale} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TemplateList({ type, cobrandedLink, partnerName, signupLink, locale }) {
  const { t } = useI18n();
  // Each template definition references translation keys for subject/body/title
  // and meta — replacing the {{LINK}}, {{PARTNER}}, {{SIGNUP}} placeholders on the client.
  const templates = useMemo(() => TEMPLATES_BY_TYPE[type] || [], [type]);

  if (templates.length === 0) {
    return <p className="text-sm text-slate-500">{t("partner_kit.no_templates")}</p>;
  }

  return templates.map((tpl) => {
    const title = t(tpl.titleKey);
    const subject = tpl.subjectKey ? t(tpl.subjectKey) : null;
    const body = t(tpl.bodyKey)
      .replaceAll("{{LINK}}", cobrandedLink)
      .replaceAll("{{PARTNER}}", partnerName)
      .replaceAll("{{SIGNUP}}", signupLink);
    const fullClipboard = subject ? `Subject: ${subject}\n\n${body}` : body;

    return (
      <Card key={tpl.id} className="border-slate-800 bg-[#0A0C1F] p-6" data-testid={`kit-template-${tpl.id}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <tpl.icon className="h-4 w-4 text-[#9ACD32]" />
            <h3 className="font-display text-base font-bold text-white">{title}</h3>
          </div>
          <CopyButton text={fullClipboard} testId={`kit-copy-${tpl.id}`} small />
        </div>
        {subject && (
          <div className="mt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("partner_kit.subject_label")}</p>
            <p className="mt-1 text-sm font-semibold text-slate-200">{subject}</p>
          </div>
        )}
        <pre className="mt-4 whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/60 p-4 font-mono text-xs leading-relaxed text-slate-200" data-testid={`kit-body-${tpl.id}`}>
{body}
        </pre>
      </Card>
    );
  });
}

function CopyButton({ text, testId, small }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size={small ? "sm" : "default"}
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success(t("partner_kit.copied"));
        setTimeout(() => setCopied(false), 1500);
      }}
      className={`rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38] ${small ? "h-8 px-3 text-xs" : ""}`}
      data-testid={testId}
    >
      {copied ? <Check className={`mr-1.5 ${small ? "h-3 w-3" : "h-4 w-4"}`} /> : <Copy className={`mr-1.5 ${small ? "h-3 w-3" : "h-4 w-4"}`} />}
      {t(copied ? "partner_kit.copied" : "partner_kit.copy")}
    </Button>
  );
}

// Template manifest. Bodies live in translations.js so they translate fully.
const TEMPLATES_BY_TYPE = {
  email: [
    { id: "email_cold",     icon: Mail, titleKey: "partner_kit.email_cold_title",     subjectKey: "partner_kit.email_cold_subject",     bodyKey: "partner_kit.email_cold_body" },
    { id: "email_followup", icon: Mail, titleKey: "partner_kit.email_followup_title", subjectKey: "partner_kit.email_followup_subject", bodyKey: "partner_kit.email_followup_body" },
    { id: "email_value",    icon: Mail, titleKey: "partner_kit.email_value_title",    subjectKey: "partner_kit.email_value_subject",    bodyKey: "partner_kit.email_value_body" },
  ],
  social: [
    { id: "social_linkedin", icon: Linkedin, titleKey: "partner_kit.social_linkedin_title", bodyKey: "partner_kit.social_linkedin_body" },
    { id: "social_twitter",  icon: Twitter,  titleKey: "partner_kit.social_twitter_title",  bodyKey: "partner_kit.social_twitter_body" },
    { id: "social_facebook", icon: Megaphone,titleKey: "partner_kit.social_facebook_title", bodyKey: "partner_kit.social_facebook_body" },
  ],
  chat: [
    { id: "chat_whatsapp", icon: MessageCircle, titleKey: "partner_kit.chat_whatsapp_title", bodyKey: "partner_kit.chat_whatsapp_body" },
    { id: "chat_dm",       icon: MessageCircle, titleKey: "partner_kit.chat_dm_title",       bodyKey: "partner_kit.chat_dm_body" },
  ],
};
